import React, { useEffect, useState } from 'react';
import {
  Box,
  Avatar,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { LockOutlined as LockIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setUser } from '../../redux/user.redux';
import { api } from '../../api/chat';
import { avatarText } from '../../utils/format';
import { anonIdOf } from '../../lib/anon';
import { deviceInfo } from '../../lib/device';
import { userBackground } from '../../lib/background';
import PanelShell from './panel-shell';

interface Props {
  onClose: () => void;
  embedded?: boolean;
}

export default function ProfilePanel({ onClose, embedded }: Props) {
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.user.user);
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [device, setDevice] = useState(deviceInfo());

  useEffect(() => {
    if (me) {
      setNickname(me.nickname || '');
      setAvatar(me.avatar || '');
      setDevice(deviceInfo());
    }
  }, [me]);

  if (!me) return null;

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const updated = await api.updateProfile({
        nickname: nickname.trim() || undefined,
        avatar: avatar.trim() || undefined,
      });
      dispatch(setUser(updated));
      setMsg({ type: 'success', text: '已保存' });
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.message || '保存失败' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PanelShell title="我的资料" onClose={onClose} embedded={embedded}>
      <Box sx={{ p: 2.5, pb: { xs: 96, md: 2.5 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            borderRadius: 2,
            background: userBackground(me.id),
            mb: 2,
          }}
        >
          <Avatar
            src={avatar || me.avatar || undefined}
            sx={{ width: 84, height: 84, fontSize: 34, bgcolor: 'primary.main', border: '3px solid #fff', boxShadow: 1 }}
          >
            {avatarText({ nickname, username: me.username })}
          </Avatar>
          <Typography sx={{ mt: 1.5, fontWeight: 600 }}>{nickname || me.username}</Typography>
          <Typography variant="caption" color="text.secondary">
            @{me.username}
          </Typography>
        </Box>

        <TextField fullWidth size="small" label="用户名" value={me.username} disabled sx={{ mb: 2 }} />
        <TextField fullWidth size="small" label="昵称" value={nickname} onChange={(e) => setNickname(e.target.value)} sx={{ mb: 2 }} />
        <TextField
          fullWidth
          size="small"
          label="头像 URL"
          placeholder="https://…"
          value={avatar}
          onChange={(e) => setAvatar(e.target.value)}
          sx={{ mb: 2 }}
        />
        {me.email && (
          <Typography variant="caption" color="text.secondary">
            {me.email}
          </Typography>
        )}
        {msg && (
          <Alert severity={msg.type} sx={{ mt: 1 }}>
            {msg.text}
          </Alert>
        )}
        <Button fullWidth variant="contained" onClick={save} disabled={saving} sx={{ mt: 2, borderRadius: 2 }}>
          {saving ? '保存中…' : '保存资料'}
        </Button>

        <Divider sx={{ my: 2.5 }} />

        {/* 个人安全信息 */}
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LockIcon fontSize="small" sx={{ color: 'primary.main' }} />
          安全信息
        </Typography>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          本地持久化数据已加密存储;消息经 WebSocket 实时传输,生产环境使用 TLS 加密。截图泄露时可凭下方匿名 ID 溯源。
        </Alert>
        <List disablePadding dense>
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="匿名 ID" secondary={anonIdOf(me.id)} secondaryTypographyProps={{ fontFamily: 'monospace' }} />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="设备" secondary={`${device.browser} · ${device.os}`} />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="平台" secondary={device.platform} />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary="屏幕" secondary={device.screen} />
          </ListItem>
        </List>

        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={async () => {
            try {
              await api.logout();
            } catch {
              /* ignore */
            }
            window.location.href = '/login';
          }}
          sx={{ mt: 2, borderRadius: 2 }}
        >
          退出登录
        </Button>
      </Box>
    </PanelShell>
  );
}
